import React, { FC, createRef, useEffect, useState } from 'react';

import {
  InputDefaultValidator,
  InputValidator
} from '@lucid/utils';
import { LucidTheme } from '@lucid/theme';

interface InputProps {
  hasFloatingLabel?: boolean;
  focus?: boolean;
  errorMessage?: string;
  hideLabel?: boolean;
  hintMessage?: string;
  label?: string;
  name: string;
  placeholder?: string;
  showValidation?: boolean;
  type?: 'email' | 'number' | 'password' | 'text';
  validator?: InputValidator;
  value: string;
  onChange?: (newValue: string) => any;
  onValidate?: (isValid: boolean) => any;
}

const Input: FC<InputProps> = ({
  hasFloatingLabel,
  focus,
  errorMessage: customErrorMessage,
  hideLabel,
  hintMessage,
  label,
  name,
  placeholder = '',
  showValidation,
  type = 'text',
  validator: validate = InputDefaultValidator,
  value,
  onChange,
  onValidate
}) => {
  let hasBeenValidatedOnce = false;
  const placeholderText = hasFloatingLabel ? '' : placeholder;
  const [hasHint, setHasHint] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [shouldFloatLabel, setShouldFloatLabel] = useState(false);

  const ref = createRef<HTMLInputElement>();

  useEffect(() => {
    if (focus && ref.current) {
      ref.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runValidator = (newValue: string) => {
    if (validate) {
      const { hasPassed, message } = validate(customErrorMessage)(newValue);
      setHasError(!hasPassed);
      setErrorMessage(message);
      if (!showValidation) {
        setHasHint(!hasPassed);
      }
      if (onValidate) {
        onValidate(hasPassed);
      }
    }
    if (onChange) {
      onChange(newValue);
    }
  };

  useEffect(() => {
    if (showValidation && !hasBeenValidatedOnce) {
      runValidator(value);
      hasBeenValidatedOnce = true;
    }
  }, [showValidation]);

  const updateValue = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const inputElement = evt.nativeEvent.srcElement as HTMLInputElement;
    const newValue = inputElement.value;
    if (newValue.length) {
      setShouldFloatLabel(true);
    } else {
      setShouldFloatLabel(false);
    }

    runValidator(newValue);
  };

  return (
    <div
      css={(theme: LucidTheme) => ({
        display: 'flex',
        flexFlow: 'column nowrap',
        width: '100%',
        fontSize: theme.fontSizes.fs16,
        fontWeight: theme.fontWeights.strong,
        position: 'relative'
      })}
    >
      {hasError && showValidation && (
        <span
          css={(theme: LucidTheme) => ({
            color: theme.colors.fuchsia[60],
            fontSize: '0.75em',
            position: 'absolute',
            top: 0,
            width: '100%',
            transform: 'translateY(-100%)'
          })}
        >
          {errorMessage}
        </span>
      )}
      {hasHint && hintMessage && (
        <span
          css={(theme: LucidTheme) => ({
            color: theme.colors.white,
            fontSize: '0.75em',
            position: 'absolute',
            width: '100%',
            backgroundColor: theme.colors.coolGrey[80],
            padding: '0.35rem',
            bottom: '-0.15rem',
            transform: 'translateY(100%)',
            borderRadius: theme.radii.textOnly
          })}
        >
          {hintMessage}
        </span>
      )}
      <div
        css={{
          display: 'flex',
          position: 'relative',
          width: '100%'
        }}
      >
        {label && !hasFloatingLabel && !hideLabel && (
          <label
            css={(theme: LucidTheme) => ({
              fontWeight: theme.fontWeights.regular,
              textAlign: 'left',
              textRendering: 'optimizeLegibility'
            })}
            htmlFor={name}
          >
            {label}
          </label>
        )}
        {!hasFloatingLabel && hideLabel && (
          <label
            css={(theme: LucidTheme) => ({
              fontWeight: theme.fontWeights.regular,
              textAlign: 'left',
              textRendering: 'optimizeLegibility',
              border: 'none',
              clip: 'rect(1px, 1px, 1px, 1px)',
              clipPath: 'inset(50%)',
              height: '1px',
              margin: '-1px',
              width: '1px',
              overflow: 'hidden',
              position: 'absolute',
              wordWrap: 'normal'
            })}
            htmlFor={name}
          >
            {label || placeholder}
          </label>
        )}
        <input
          ref={ref}
          css={(theme: LucidTheme) => [
            {
              '&:focus + label': {
                color: theme.colors.secondary
              },
              display: 'block',
              width: '100%',
              padding: '0.3375rem 0.75rem',
              height: '50px',
              fontSize: '1em',
              color: theme.colors.coolGrey[70],
              outline: 'none',
              border: `1px solid ${theme.colors.coolGrey[30]}`,
              backgroundColor: theme.colors.white,
              borderRadius: theme.radii.base,
              '&:hover': {
                borderColor: theme.colors.coolGrey[20]
              },
              '&:focus': {
                borderColor: theme.colors.secondary
              },
              '&:-webkit-autofill': {
                boxShadow: `0 0 0 1000px ${theme.colors.white} inset !important`
              }
            },
            hasError && showValidation && {
              borderColor: theme.colors.fuchsia[60],
              backgroundColor: theme.colors.red[5],
              '&:hover': {
                borderColor: theme.colors.red[60]
              },
              '&:focus': {
                borderColor: theme.colors.red[60]
              },
            },
            shouldFloatLabel && {
              transition: 'padding 0.05s ease-out',
              paddingTop: '1.2em'
            }
          ]}
          // eslint-disable-next-line react/jsx-sort-props
          id={name}
          name={name}
          onChange={updateValue}
          placeholder={placeholderText}
          type={type}
          value={value}
        />
        {hasFloatingLabel && label && !hideLabel && (
          <label
            css={(theme: LucidTheme) => [
              {
                fontWeight: theme.fontWeights.regular,
                textAlign: 'left',
                textRendering: 'optimizeLegibility',
                position: 'absolute',
                lineHeight: 1,
                display: 'block',
                top: 0,
                bottom: 0,
                left: '0.75rem',
                right: '1px',
                margin: 'auto',
                height: '1em',
                transition: 'transform 0.15s ease-out',
                color: theme.colors.coolGrey[30],
                willChange: 'transform',
                userSelect: 'none',
                pointerEvents: 'none',
                transformOrigin: '0 0'
              },
              shouldFloatLabel && {
                transform: 'translateY(calc(-55%)) scale(0.75)',
                transition: 'transform 0.15s ease-in color 0.15s ease-in'
              }
            ]}
            htmlFor={name}
          >
            {label}
          </label>
        )}
      </div>
    </div>
  );
};

export default Input;
